import { useState, useEffect, ChangeEvent } from "react";
import { Button, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import useUserAuth from "../../../../auth/hook/useUserAuth";
import { FilterSpendingDemand, SpendingDemandTypes } from "../../../types/spendingDemand.types";
import SpendingDemandListFilterView, {
  StatusItem,
  TypeItem,
} from "./SpendingDemandListFilterView/SpendingDemandListFilterView";
import SearchPickProject from "../../../../../features/project/components/SearchPickProject";
import SearchPickUser from "../../../../../features/user/components/SearchPickUser";
import { useSpendingDemandManager } from "../../../hooks";
import { convertFilterSpendingDemandToGenericFilter, convertGenericFilterToFilterSpendingDemand } from "../../../utils/convertFilterSpendingDemandToGenericFilter";
import { useBoxAssociationFromBoxId, useSingleBoxAssociation } from "../../../../boxAssociation/hooks/useBoxAssociation";

export const SpendingDemandManagerPresenterFilter = () => {
  const { allSpendingDemandList } = useSpendingDemandManager();
  const { userInfo } = useUserAuth();
  const { setFilter, setSearch, setDescSearch } = allSpendingDemandList;

  // Convert current filter from generic format to FilterSpendingDemand for local state
  const currentGenericFilter = convertGenericFilterToFilterSpendingDemand(
    allSpendingDemandList.currentFilterObject,
    allSpendingDemandList.currentFilter
  );

  // États locaux pour les filtres de date et montant
  const [dateRange, setDateRange] = useState<{ min?: string; max?: string }>({
    min: currentGenericFilter?.date?.min || '',
    max: currentGenericFilter?.date?.max || '',
  });

  const [amountRange, setAmountRange] = useState<{ min?: number | ''; max?: number | '' }>({
    min: currentGenericFilter?.amountTTC?.min || '',
    max: currentGenericFilter?.amountTTC?.max || '',
  });

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    currentGenericFilter?.projectId && currentGenericFilter.projectId.length > 0 ? currentGenericFilter.projectId[0] : null
  );

  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    currentGenericFilter?.userId && currentGenericFilter.userId.length > 0 ? currentGenericFilter.userId[0] : null
  );

  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

  // État pour le BoxId (saisie utilisateur)
  const [boxIdInput, setBoxIdInput] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // État pour le Readable ID
  const [readableIdInput, setReadableIdInput] = useState<string>(
    currentGenericFilter?.readableId || ''
  );

  // État pour la recherche plein-texte Description (param descSearch)
  const [descriptionInput, setDescriptionInput] = useState<string>(
    allSpendingDemandList.descSearchValue || ''
  );

  // Synchroniser descriptionInput si descSearch change depuis l'URL (reset externe)
  useEffect(() => {
    setDescriptionInput(allSpendingDemandList.descSearchValue || '');
  }, [allSpendingDemandList.descSearchValue]);

  // Debounce de la Description -> param descSearch (~400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = descriptionInput.trim();
      const nextValue = trimmed !== '' ? trimmed : undefined;
      const currentValue = allSpendingDemandList.descSearchValue || undefined;
      if (nextValue !== currentValue) {
        setDescSearch(nextValue);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [descriptionInput, allSpendingDemandList.descSearchValue, setDescSearch]);

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDescriptionInput(e.target.value);
  };

  // Récupérer le boxId à partir du boxAssociationId si un filtre existe déjà
  const existingBoxAssociationIdFromFilter = currentGenericFilter?.boxAssociationId && currentGenericFilter.boxAssociationId.length > 0
    ? currentGenericFilter.boxAssociationId[0]
    : undefined;
  
  const {
    boxAssociation: existingBoxAssociationFromFilter,
  } = useSingleBoxAssociation(existingBoxAssociationIdFromFilter, { disableQuery: !existingBoxAssociationIdFromFilter });
  
  // État pour le debounce du BoxId
  const [debouncedBoxId, setDebouncedBoxId] = useState<string | undefined>(undefined);
  
  // Récupérer le boxAssociationId à partir du boxId
  const {
    boxAssociation: boxAssociationFromBoxId,
    isLoading: isValidatingBox,
    isError: isBoxError,
  } = useBoxAssociationFromBoxId(debouncedBoxId, { disableQuery: !debouncedBoxId });
  
  // Debounce du BoxId
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBoxId(boxIdInput && boxIdInput.trim() !== '' ? boxIdInput.trim() : undefined);
    }, 500);

    return () => clearTimeout(timer);
  }, [boxIdInput]);

  // Text search for partial matches (box ID only; readable ID uses whereQuery LIKE).
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchTerms = [boxIdInput.trim()].filter(Boolean);
      const nextSearchValue = searchTerms.length > 0 ? searchTerms.join(" ") : undefined;
      const currentSearchValue = allSpendingDemandList.sreachValue || undefined;

      if (nextSearchValue !== currentSearchValue) {
        setSearch(nextSearchValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [boxIdInput, allSpendingDemandList.sreachValue, setSearch]);
  
  // Mettre à jour le filtre quand boxAssociationId est récupéré
  useEffect(() => {
    if (boxAssociationFromBoxId?.id) {
      // Vérifier si le boxAssociationId a changé pour éviter les boucles infinies
      const currentBoxAssociationId = localFilter?.boxAssociationId?.[0];
      if (currentBoxAssociationId !== boxAssociationFromBoxId.id) {
        const updatedFilter = {
          ...localFilter,
          boxAssociationId: [boxAssociationFromBoxId.id],
        };
        setLocalFilter(updatedFilter);
        const genericFilter = convertFilterSpendingDemandToGenericFilter(updatedFilter);
        setFilter(genericFilter);
      }
    } else if (debouncedBoxId === undefined && !boxIdInput && localFilter?.boxAssociationId) {
      // Si le champ est vide et qu'il y avait un filtre, le retirer
      const updatedFilter = {
        ...localFilter,
        boxAssociationId: undefined,
      };
      setLocalFilter(updatedFilter);
      const genericFilter = convertFilterSpendingDemandToGenericFilter(updatedFilter);
      setFilter(genericFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxAssociationFromBoxId?.id, debouncedBoxId, boxIdInput]);

  // États pour la modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  
  // État pour l'accordéon
  const [isExpanded, setIsExpanded] = useState(false);

  // Local filter state
  const [localFilter, setLocalFilter] = useState<FilterSpendingDemand>(currentGenericFilter || {});

  // Sync local filter state when generic filter changes (e.g., from URL or external changes)
  useEffect(() => {
    const updatedFilter = convertGenericFilterToFilterSpendingDemand(
      allSpendingDemandList.currentFilterObject,
      allSpendingDemandList.currentFilter
    );
    
    if (updatedFilter) {
      setLocalFilter(updatedFilter);
      setDateRange({
        min: updatedFilter.date?.min || '',
        max: updatedFilter.date?.max || '',
      });
      setAmountRange({
        min: updatedFilter.amountTTC?.min || '',
        max: updatedFilter.amountTTC?.max || '',
      });
      setSelectedProjectId(
        updatedFilter.projectId && updatedFilter.projectId.length > 0 ? updatedFilter.projectId[0] : null
      );
      setSelectedUserId(
        updatedFilter.userId && updatedFilter.userId.length > 0 ? updatedFilter.userId[0] : null
      );
      setReadableIdInput(updatedFilter.readableId || '');
      // Ne pas réinitialiser boxIdInput ici, il sera mis à jour par l'effet suivant
    } else {
      // Reset to empty if no filter
      setLocalFilter({});
      setDateRange({ min: '', max: '' });
      setAmountRange({ min: '', max: '' });
      setSelectedProjectId(null);
      setSelectedUserId(null);
      setBoxIdInput('');
      setReadableIdInput('');
    }
  }, [allSpendingDemandList.currentFilter, allSpendingDemandList.currentFilterObject]);
  
  // Mettre à jour boxIdInput quand on récupère le boxId depuis le boxAssociationId existant (seulement au chargement initial)
  useEffect(() => {
    if (isInitialLoad && existingBoxAssociationFromFilter?.boxId) {
      setBoxIdInput(existingBoxAssociationFromFilter.boxId);
      setIsInitialLoad(false);
    } else if (!existingBoxAssociationIdFromFilter && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [existingBoxAssociationFromFilter?.boxId, existingBoxAssociationIdFromFilter, isInitialLoad]);


  const statusList: StatusItem[] = [
    {
      value: "WAITING_VALIDATION",
      label: "En attente de traitement / À traiter",
    },
    {
      value: "VALIDATE",
      label: "Validé",
    },
    {
      value: "SAVED",
      label: "Sauvegardé",
    },
    {
      value: "WAITING_UPDATE",
      label: "En attente de correction",
    },
    {
      value: "REFUSED",
      label: "Refusé",
    },
  ];

  const typeList: TypeItem[] = [
    {
      value: "PROJECT",
      label: "Projet",
    },
    {
      value: "BOX",
      label: "Box",
    },
    {
      value: "CURRENT",
      label: "Courant",
    },
  ];

  const convertFilter = (filter: FilterSpendingDemand) => {
    const result = { ...filter };

    if (filter.status && filter.status?.includes('WAITING_VALIDATION')) {
      filter.status = ['WAITING_VALIDATION'];
      result.managerWaitingValidation = [userInfo?.user_employee_id || ""];
    }

    return result;
  };

  // Helper function to update the generic filter
  const updateGenericFilter = (newFilter: FilterSpendingDemand) => {
    setLocalFilter(newFilter);
    const genericFilter = convertFilterSpendingDemandToGenericFilter(newFilter);
    setFilter(genericFilter);
  };

  const handleClickStatus = (valueClicked: string) => {
    if (valueClicked) {
      const currentStatus = localFilter?.status || [];

      if (localFilter?.status?.includes(valueClicked)) {
        const updatedFilter = convertFilter({
          ...localFilter,
          status: currentStatus.filter((status) => status !== valueClicked),
        });
        updateGenericFilter(updatedFilter);
      } else {
        const updatedFilter = convertFilter({ ...localFilter, status: [...currentStatus, valueClicked] });
        updateGenericFilter(updatedFilter);
      }
    }
  };

  const handleClickType = (valueClicked: string) => {
    if (valueClicked) {
      const currentType = localFilter?.type || [];

      if (localFilter?.type?.includes(valueClicked as SpendingDemandTypes)) {
        const updatedFilter = {
          ...localFilter,
          type: currentType.filter((type) => type !== valueClicked),
        };
        updateGenericFilter(updatedFilter);
      } else {
        const updatedFilter = { ...localFilter, type: [...currentType, valueClicked as SpendingDemandTypes] };
        updateGenericFilter(updatedFilter);
      }
    }
  };

  const handleDateChange = (field: 'min' | 'max', value: string) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);

    const dateFilter = {
      min: newDateRange.min || undefined,
      max: newDateRange.max || undefined,
    };

    const updatedFilter = {
      ...localFilter,
      date: dateFilter,
    };
    updateGenericFilter(updatedFilter);
  };

  const handleAmountChange = (field: 'min' | 'max', value: number | '') => {
    const newAmountRange = { ...amountRange, [field]: value };
    setAmountRange(newAmountRange);

    const amountFilter = {
      min: newAmountRange.min === '' ? undefined : newAmountRange.min as number,
      max: newAmountRange.max === '' ? undefined : newAmountRange.max as number,
    };

    const updatedFilter = {
      ...localFilter,
      amountTTC: amountFilter,
    };
    updateGenericFilter(updatedFilter);
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId || null);
    
    const projectFilter = projectId ? [projectId] : undefined;
    
    const updatedFilter = {
      ...localFilter,
      projectId: projectFilter,
    };
    updateGenericFilter(updatedFilter);
  };

  const handleProjectSelect = (project: any) => {
    setSelectedProjectId(project.project_id);
    setSelectedProjectName(project.project_name);
    setIsProjectModalOpen(false);
    
    const updatedFilter = {
      ...localFilter,
      projectId: [project.project_id],
    };
    updateGenericFilter(updatedFilter);
  };

  const handleOpenProjectModal = () => {
    setIsProjectModalOpen(true);
  };

  const handleCloseProjectModal = () => {
    setIsProjectModalOpen(false);
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId || null);
    
    const userFilter = userId ? [userId] : undefined;
    
    const updatedFilter = {
      ...localFilter,
      userId: userFilter,
    };
    updateGenericFilter(updatedFilter);
  };

  const handleUserSelect = (user: any) => {
    setSelectedUserId(user.user_id);
    setSelectedUserName(`${user.username} ${user.lastname}`);
    setIsUserModalOpen(false);
    
    const updatedFilter = {
      ...localFilter,
      userId: [user.user_id],
    };
    updateGenericFilter(updatedFilter);
  };

  const handleOpenUserModal = () => {
    setIsUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
  };

  const handleBoxIdChange = (value: string) => {
    setBoxIdInput(value);
    // Si le champ est vidé, retirer le filtre immédiatement
    if (!value || value.trim() === '') {
      const updatedFilter = {
        ...localFilter,
        boxId: undefined,
        boxAssociationId: undefined,
      };
      updateGenericFilter(updatedFilter);
    }
  };

  const handleReadableIdChange = (value: string) => {
    setReadableIdInput(value);
    
    const updatedFilter = {
      ...localFilter,
      boxId: boxIdInput && boxIdInput.trim() !== '' ? boxIdInput.trim() : undefined,
      readableId: value && value.trim() !== '' ? value.trim() : undefined,
    };
    updateGenericFilter(updatedFilter);
  };

  const handleClearFilters = () => {
    setDateRange({ min: '', max: '' });
    setAmountRange({ min: '', max: '' });
    setSelectedProjectId(null);
    setSelectedProjectName(null);
    setSelectedUserId(null);
    setSelectedUserName(null);
    setBoxIdInput('');
    setReadableIdInput('');
    setDescriptionInput('');
    // Purge immédiate du param URL descSearch (sans attendre le debounce ~400ms)
    setDescSearch(undefined);
    const emptyFilter: FilterSpendingDemand = {
      status: [],
      type: [],
      projectId: undefined,
      userId: undefined,
      boxId: undefined,
      boxAssociationId: undefined,
      readableId: undefined,
      date: { min: undefined, max: undefined },
      amountTTC: { min: undefined, max: undefined },
    };
    updateGenericFilter(emptyFilter);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleApplyFilters = () => {
    // Appliquer les filtres et fermer la modal
    setIsModalOpen(false);
  };

  return (
    <Box>
      {/* Modal de filtres */}
      <SpendingDemandListFilterView
        handleClickStatus={handleClickStatus}
        handleClickType={handleClickType}
        handleDateChange={handleDateChange}
        handleAmountChange={handleAmountChange}
        handleProjectChange={handleProjectChange}
        handleOpenProjectModal={handleOpenProjectModal}
        handleUserChange={handleUserChange}
        handleOpenUserModal={handleOpenUserModal}
        handleBoxIdChange={handleBoxIdChange}
        handleReadableIdChange={handleReadableIdChange}
        handleDescriptionChange={handleDescriptionChange}
        handleClearFilters={handleClearFilters}
        handleCloseModal={handleCloseModal}
        handleApplyFilters={handleApplyFilters}
        isModalOpen={isModalOpen}
        statusList={statusList}
        typeList={typeList}
        currentStatusSelected={localFilter?.status || []}
        currentTypeSelected={localFilter?.type || []}
        currentProjectSelected={selectedProjectId}
        selectedProjectName={selectedProjectName}
        currentUserSelected={selectedUserId}
        selectedUserName={selectedUserName}
        boxIdInput={boxIdInput}
        boxIdValidationStatus={{
          isValid: !!boxAssociationFromBoxId?.id,
          isLoading: isValidatingBox,
          isError: isBoxError,
        }}
        readableIdInput={readableIdInput}
        descriptionInput={descriptionInput}
        dateRange={dateRange}
        amountRange={amountRange}
        isExpanded={isExpanded}
        onToggleExpanded={handleToggleExpanded}
      />

      {/* Modal de sélection de projet */}
      <Dialog
        open={isProjectModalOpen}
        onClose={handleCloseProjectModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="h2" sx={{ 
            fontWeight: 500,
            color: '#333333'
          }}>
            Sélectionner un projet
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ height: '400px' }}>
            <SearchPickProject
              setProject={handleProjectSelect}
              heightListResult="350px"
              scrollable={true}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleCloseProjectModal}
            variant="outlined"
            sx={{ 
              borderColor: '#cccccc',
              color: '#666666',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de sélection d'utilisateur */}
      <Dialog
        open={isUserModalOpen}
        onClose={handleCloseUserModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="h2" sx={{ 
            fontWeight: 500,
            color: '#333333'
          }}>
            Sélectionner un utilisateur
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ height: '400px' }}>
            <SearchPickUser
              onClickEmployeeFound={handleUserSelect}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleCloseUserModal}
            variant="outlined"
            sx={{ 
              borderColor: '#cccccc',
              color: '#666666',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

